import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button, Card, ErrorNote } from "./primitives";
import { api } from "../lib/api";

/**
 * Where a playbook of your own starts: the last tile under Other.
 *
 * It had a page of its own, beside a second copy of every tile on the start
 * page and a panel of figure types to tick. The tiles were the start page's
 * already, and the figure panel narrowed a choice almost nobody made — none
 * ticked meant all of them — so the page went and this is what survived. A
 * playbook written here may draw every figure type, and lands under Other,
 * beside the tile that made it.
 */
export function NewPlaybookTile({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className={`tile group flex min-h-48 flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-5 text-center transition-colors hover:bg-surface-2 ${
        open ? "border-accent" : "border-line-strong"
      }`}
    >
      <svg
        aria-hidden
        viewBox="0 0 40 40"
        className="size-10 text-muted transition-colors group-hover:text-ink"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <circle cx="20" cy="20" r="18" />
        <path d="M20 12v16M12 20h16" />
      </svg>
      <span className="text-sm font-medium text-ink">
        {open ? "Close the new playbook" : "Create a new playbook"}
      </span>
      <span className="max-w-56 text-xs leading-relaxed text-ink-2">
        Your own method, run like any other. Its findings stay with you.
      </span>
    </button>
  );
}

/** Write one. Every figure type is available to it. */
export function CreatePlaybookForm({ onDone }: { onDone: () => void }) {
  const client = useQueryClient();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [intent, setIntent] = useState("");
  const [audience, setAudience] = useState("");
  const [workstreams, setWorkstreams] = useState("");
  const [useModel, setUseModel] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      api.createPlaybook({
        title,
        description,
        intent,
        // None named means all of them, which is what the panel that used to
        // narrow this was left at almost every time.
        figures: [],
        audience: audience.split(",").map((a) => a.trim()).filter(Boolean),
        workstreams: workstreams.split("\n").map((w) => w.trim()).filter(Boolean),
        draft_with_model: useModel,
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["playbooks"] });
      onDone();
    },
  });

  return (
    <Card title="A playbook of your own" className="mt-4">
      <form
        className="space-y-4 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <div>
          <label className="field-label" htmlFor="pb-title">
            Title
          </label>
          <input
            id="pb-title"
            className="field"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Pricing review"
            required
            autoFocus
          />
        </div>
        <div>
          <label className="field-label" htmlFor="pb-description">
            One line, for the tile
          </label>
          <input
            id="pb-description"
            className="field"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Where the price is being left on the table."
            required
          />
        </div>
        <div>
          <label className="field-label" htmlFor="pb-intent">
            What should it do?
          </label>
          <textarea
            id="pb-intent"
            className="field min-h-24"
            value={intent}
            onChange={(e) => setIntent(e.target.value)}
            placeholder="e.g. Establish what the client charges, what the market bears, and where the gap is."
            required
          />
          <p className="mt-1 text-xs text-muted">
            Your words reach the agent's own brief unchanged. This is the part
            of the playbook that is genuinely yours.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="field-label" htmlFor="pb-audience">
              Who it is for
            </label>
            <input
              id="pb-audience"
              className="field"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder="e.g. Consultants, PE investors"
            />
            <p className="mt-1 text-xs text-muted">Comma separated, three at most.</p>
          </div>
          <div>
            <label className="field-label" htmlFor="pb-workstreams">
              Workstreams
            </label>
            <textarea
              id="pb-workstreams"
              className="field min-h-20"
              value={workstreams}
              onChange={(e) => setWorkstreams(e.target.value)}
              placeholder={"e.g. Current pricing\nWillingness to pay"}
            />
            <p className="mt-1 text-xs text-muted">One per line.</p>
          </div>
        </div>

        <label className="flex cursor-pointer items-start gap-2.5 text-sm">
          <input
            type="checkbox"
            checked={useModel}
            onChange={(e) => setUseModel(e.target.checked)}
            className="mt-1 shrink-0"
          />
          <span>
            Let the agent design the workstreams
            <span className="mt-0.5 block text-xs text-muted">
              Reads what you wrote above and proposes the strands of work.
              Anything you filled in yourself is kept. Costs a small model call
              on your credit; leave it off and the playbook is written from a
              template, instantly and for nothing.
            </span>
          </span>
        </label>

        {create.isError && <ErrorNote error={create.error} />}
        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending
              ? useModel
                ? "Designing…"
                : "Writing…"
              : "Create playbook"}
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
